import { useState, useEffect, useCallback, useContext, useRef } from "react";
import axios from "axios";
import { getAccessToken } from "../../api/clients/tokenAccessor";
import { useAuthContext } from "../../context/AuthContext";
import { BodycamContext } from "../../context/BodycamContext";
import { profileClient } from "../../api/clients";
import { useTemplateChat } from "../../hooks/useTemplateChat";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { validateFile, MAX_FILES } from "./utils";
import type { Template, UploadQueueItem, WorkflowStep } from "./types";

export function useTemplateUploadPage() {
  const { userInfo, customReports } = useContext(BodycamContext);
  const [, setCustomReportTypes] = customReports;
  const [userProfile] = userInfo;
  const { organizationId } = useAuthContext();

  const [stations, setStations] = useState<
    { stationId: string; stationName: string }[]
  >([]);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(
    organizationId,
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [templateSearch, setTemplateSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [, setIsAtChatBottom] = useState(true);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const prevMessageCountRef = useRef(0);

  // Workflow state
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>("upload");
  const [reviewContent, setReviewContent] = useState("");
  const [generatedExamples, setGeneratedExamples] = useState<string[]>([]);
  const [signOffDialogOpen, setSignOffDialogOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );
  const [editingName, setEditingName] = useState("");

  const rmsApiBaseUrl = import.meta.env.VITE_RMS_API_BASE_URL;
  const aiApiBaseUrl = import.meta.env.VITE_AI_API_BASE_URL ?? "";

  const {
    snackBarMessage,
    snackBarMode,
    showError,
    showSuccess,
    handleCloseSnackbar,
  } = useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  const templateChat = useTemplateChat({
    jobId,
    onTemplateUpdate: (newTemplate, newExamples) => {
      setReviewContent(newTemplate);
      setGeneratedExamples(newExamples);
    },
    showError,
  });

  // Chat scroll helpers
  const scrollChatToBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, []);

  const checkIfAtBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }, []);

  const handleChatScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    setIsAtChatBottom(atBottom);
    if (atBottom) setShowScrollArrow(false);
  }, [checkIfAtBottom]);

  // Auto-scroll on new messages
  useEffect(() => {
    const count = templateChat.messages.length;
    const prev = prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    if (count > prev && count > 0) {
      const lastMsg = templateChat.messages[count - 1];
      if (lastMsg.role === "user") {
        scrollChatToBottom();
      } else if (lastMsg.role === "assistant") {
        if (!checkIfAtBottom()) {
          setShowScrollArrow(true);
        } else {
          scrollChatToBottom();
        }
      }
    }
  }, [templateChat.messages, scrollChatToBottom, checkIfAtBottom]);

  // Sync selectedStationId when organizationId arrives
  useEffect(() => {
    if (organizationId) {
      setSelectedStationId(organizationId);
    }
  }, [organizationId]);

  // Fetch all stations
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await profileClient.get("/v1/stations");
        if (!cancelled && response.data?.stations?.length > 0) {
          setStations(
            response.data.stations.map((s: any) => ({
              stationId: s.id,
              stationName: s.name,
            })),
          );
        }
      } catch (err) {
        console.error("Failed to fetch stations:", err);
        if (!cancelled && organizationId && userProfile?.stationName) {
          setStations([
            { stationId: organizationId, stationName: userProfile.stationName },
          ]);
        }
      } finally {
        if (!cancelled) setStationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, userProfile?.stationName]);

  // Fetch templates
  const fetchTemplates = useCallback(
    async (showLoading = true) => {
      if (!selectedStationId) return;
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      if (showLoading) setIsLoading(true);
      try {
        const token = await getAccessToken();
        const response = await axios.get(
          `${rmsApiBaseUrl}/templates?stationId=${selectedStationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const data = response.data;
        const items = Array.isArray(data) ? data : (data?.templates ?? []);
        const mapped: Template[] = items
          .filter((item: any) => item.template)
          .map((item: any) => ({
            id: item.template.templateId,
            templateId: item.template.templateId,
            name: item.template.name,
            stationId: item.template.stationId,
            content: item.template.content,
            uploadedBy: item.template.uploadedBy,
            uploadedDate: item.template.createdAt,
            status: "active" as const,
          }));
        setTemplates(mapped);
      } catch (err: any) {
        if (axios.isCancel(err)) return;
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "An error occurred while fetching templates. Please try again.";
        showError(errorMessage);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [showError, selectedStationId],
  );

  // File handling
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    handleFilesAdd(Array.from(files));
    event.target.value = "";
  };

  const handleFilesAdd = (files: File[]) => {
    const currentCount = uploadQueue.length;
    if (currentCount + files.length > MAX_FILES) {
      showError(
        `You can upload a maximum of ${MAX_FILES} files. You already have ${currentCount} file${currentCount === 1 ? "" : "s"} queued.`,
      );
      return;
    }

    const existingNames = new Set(
      uploadQueue.map((item) => `${item.file.name}_${item.file.size}`),
    );
    const uniqueFiles = files.filter((file) => {
      const key = `${file.name}_${file.size}`;
      if (existingNames.has(key)) return false;
      existingNames.add(key);
      return true;
    });

    if (uniqueFiles.length === 0) return;

    const newItems: UploadQueueItem[] = uniqueFiles.map((file) => {
      const validation = validateFile(file);
      return {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        progress: 0,
        status: validation.valid ? "pending" : "error",
        errorMessage: validation.error,
      };
    });

    setUploadQueue((prev) => [...prev, ...newItems]);

    newItems
      .filter((item) => item.status === "pending")
      .forEach((item) => {
        setUploadQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? { ...q, status: "completed", progress: 100 }
              : q,
          ),
        );
      });
  };

  const handleSubmitForProcessing = async () => {
    const stagedFiles = uploadQueue.filter((i) => i.status === "completed");
    if (stagedFiles.length === 0) return;

    setWorkflowStep("processing");
    setProcessingError(null);

    try {
      const token = await getAccessToken();
      const authHeaders = { Authorization: `Bearer ${token}` };

      const fileMeta = stagedFiles.map((item) => ({
        fileName: item.file.name,
        fileSize: item.file.size,
        contentType: item.file.type || "application/octet-stream",
      }));

      const { data } = await axios.post(
        "/ai/upload-template",
        { files: fileMeta },
        { headers: { ...authHeaders, "Content-Type": "application/json" } },
      );

      const { jobId: newJobId, uploads } = data;

      const uploadsByName = new Map<string, string>(
        uploads.map((u: { fileName: string; presignedUrl: string }) => [
          u.fileName,
          u.presignedUrl,
        ]),
      );
      await Promise.all(
        stagedFiles.map((item) => {
          const presignedUrl = uploadsByName.get(item.file.name);
          if (!presignedUrl) {
            throw new Error(
              `No presigned URL returned for file: ${item.file.name}`,
            );
          }
          return axios.put(presignedUrl, item.file, {
            headers: {
              "Content-Type": item.file.type || "application/octet-stream",
            },
          });
        }),
      );

      await axios.post(
        "/ai/process-template",
        { jobId: newJobId },
        { headers: { ...authHeaders, "Content-Type": "application/json" } },
      );

      setJobId(newJobId);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to submit files for processing";
      setWorkflowStep("upload");
      showError(errorMessage);
    }
  };

  const handleRemoveFromQueue = (id: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id));
  };

  // Drag and drop
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) handleFilesAdd(files);
  };

  // Delete
  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      const templateIdentifier =
        templateToDelete.id || templateToDelete.templateId;
      if (!templateIdentifier) {
        showError("Template identifier not found for deletion.");
        setIsDeleting(false);
        return;
      }

      const token = await getAccessToken();
      await axios.delete(`${rmsApiBaseUrl}/template`, {
        data: {
          templateId: templateIdentifier,
          stationId: selectedStationId,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const deletedName = templateToDelete.fileName || templateToDelete.name;
      showSuccess(`${deletedName ?? "Template"} deleted successfully`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates(false);
      setCustomReportTypes(null);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to delete template. Please try again.";
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Rename
  const handleRename = async (template: Template) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === template.name) {
      setEditingTemplateId(null);
      return;
    }
    try {
      const token = await getAccessToken();
      await axios.put(
        `${rmsApiBaseUrl}/template`,
        {
          template: {
            templateId: template.templateId,
            stationId: template.stationId ?? selectedStationId,
            name: trimmed,
            content: template.content,
            recordType: "Template",
          },
          examples: [],
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEditingTemplateId(null);
      fetchTemplates(false);
      setCustomReportTypes(null);
    } catch (err: any) {
      showError(err.response?.data?.error ?? "Failed to rename template");
    }
  };

  // Sign off
  const handleSignOff = async () => {
    try {
      const token = await getAccessToken();
      await axios.put(
        `${rmsApiBaseUrl}/template`,
        {
          template: {
            stationId: selectedStationId,
            name: uploadQueue[0]?.file?.name ?? "Uploaded Template",
            content: reviewContent,
            uploadedBy: userProfile
              ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
              : undefined,
          },
          examples: generatedExamples,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccess("Template saved successfully");
      setSignOffDialogOpen(false);
      setWorkflowStep("complete");
      fetchTemplates(false);
      setCustomReportTypes(null);
    } catch (err: any) {
      showError(err.response?.data?.error ?? "Failed to save template");
      setSignOffDialogOpen(false);
    }
  };

  // Download
  const handleDownload = async (template: Template) => {
    try {
      const templateIdentifier = template.id || template.templateId;
      if (!templateIdentifier) {
        showError("Template identifier not found for download.");
        return;
      }

      const response = await axios.get(
        `/api/templates/downloadTemplate/${templateIdentifier}`,
        { responseType: "blob" },
      );

      const disposition = response.headers["content-disposition"] as
        | string
        | undefined;

      const { getFilenameFromDisposition } = await import("./utils");
      const downloadName = getFilenameFromDisposition(
        disposition,
        template.fileName || template.name || "template.txt",
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const templateName = template.fileName || template.name || "template";
      showSuccess(`Download started for ${templateName}`);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to download template. Please try again.";
      showError(errorMessage);
    }
  };

  // Pagination
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch on mount
  useEffect(() => {
    handleCloseSnackbar();
    fetchTemplates();
  }, [fetchTemplates]);

  // Poll for template job completion
  useEffect(() => {
    if (workflowStep !== "processing" || !jobId) return;

    const interval = setInterval(async () => {
      try {
        const token = await getAccessToken();
        const response = await axios.get("/ai/get-template-job", {
          params: { jobId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data;

        if (data.status === "completed" && data.result) {
          setReviewContent(data.result.template);
          setGeneratedExamples(data.result.generatedExamples ?? []);
          templateChat.resetChat();
          setWorkflowStep("review");
          clearInterval(interval);
        } else if (data.status === "failed") {
          setWorkflowStep("upload");
          showError(data.error ?? "Template generation failed");
          clearInterval(interval);
        }
      } catch (err: any) {
        if (!err.response) return;
        const message =
          err.response?.data?.error ||
          err.message ||
          "Template processing failed";
        clearInterval(interval);
        setWorkflowStep("upload");
        showError(message);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [workflowStep, jobId, aiApiBaseUrl]);

  const filteredTemplates = templateSearch
    ? templates.filter((t) => {
        const search = templateSearch.toLowerCase();
        const name = (t.fileName || t.name || "").toLowerCase();
        return name.includes(search);
      })
    : templates;

  const paginatedTemplates = filteredTemplates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const resetWorkflow = () => {
    setWorkflowStep("upload");
    setUploadQueue([]);
    setReviewContent("");
    setGeneratedExamples([]);
    templateChat.resetChat();
    setJobId(null);
    setProcessingError(null);
  };

  return {
    // Station
    stations,
    stationsLoading,
    selectedStationId,
    setSelectedStationId,
    setTemplates,
    setPage,

    // Templates
    templates,
    filteredTemplates,
    paginatedTemplates,
    isLoading,
    page,
    rowsPerPage,
    templateSearch,
    setTemplateSearch,
    fetchTemplates,
    handleChangePage,
    handleChangeRowsPerPage,

    // Upload
    uploadQueue,
    isDragOver,
    fileInputRef,
    handleFileSelect,
    handleFilesAdd,
    handleSubmitForProcessing,
    handleRemoveFromQueue,
    handleDragOver,
    handleDragLeave,
    handleDrop,

    // Delete
    deleteDialogOpen,
    setDeleteDialogOpen,
    templateToDelete,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm,

    // Rename
    editingTemplateId,
    setEditingTemplateId,
    editingName,
    setEditingName,
    handleRename,

    // Download
    handleDownload,

    // Preview
    previewTemplate,
    setPreviewTemplate,

    // Workflow
    workflowStep,
    setWorkflowStep,
    reviewContent,
    generatedExamples,
    processingError,
    setProcessingError,
    jobId,
    setJobId,
    resetWorkflow,

    // Sign off
    signOffDialogOpen,
    setSignOffDialogOpen,
    handleSignOff,

    // Chat
    templateChat,
    chatContainerRef,
    handleChatScroll,
    showScrollArrow,
    setShowScrollArrow,
    scrollChatToBottom,

    // Snackbar
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
    snackbarPosition,
  };
}
